/* eslint-disable object-curly-newline, object-property-newline, no-nested-ternary */
//
// Graded tone adjuster -- programmatic_v2 Spike G port.
//
// The legacy scoreTone returns a value in {0, 0.5, 1.0}. Looking at the
// 1,000-sample Claude data, the engine missed 60/160 cases where Claude
// scored 1.0 (balance signal not picked up) and 15/16 cases where Claude
// scored 0 (soft negative tone missed).
//
// Two additional pattern lists (growth + negative) close some of those
// gaps. Validated against the Python prototype: tone r 0.502 -> 0.552
// (+0.050) on 1,000-sample native-3D; +0.003 on 17k corpus (small but
// consistently positive).

const EXTRA_GROWTH_PATTERNS = [
  // Past-tense growth: "has improved", "improved", "improvement"
  /\b(has |have |had )?improved\b/i,
  /\bimprov(ing|ement|ements)\b/i,
  // Forward direction: "be more X", "more X"
  /\b(be more|being more|become more|to be more) (assertive|vocal|engaged|proactive|involved|active|present|focused|consistent|organized|specific|confident|patient|direct|clear|attentive|prepared)\b/i,
  // Encouragement / advice
  /\b(encourage|advise|suggest|recommend) (you|him|her|them)? ?(to)\b/i,
  /\bi (would|d) (encourage|like|love) (you|him|her|them) to\b/i,
  // "asking how we can improve / how to do X"
  /\b(asking|seeking|considering) (how) (we|the team|i) (can|could|should|might)\b/i,
  // "don't be afraid to" / "feel free to" / "try to be more"
  /\b(don't be afraid to|feel free to|try to be more|try being more)\b/i,
  // "would have liked" / "would have benefited" (gentle critique)
  /\b(would have|would like to|would love to) (liked|seen|been|preferred|wished)\b/i,
  // "speak up more" / "participate more" / "share more"
  /\b(speak (up |out )?more|participate more|share more|engage more|contribute more|involve yourself more|step (up |out )?more)\b/i,
  // "willing to take on" / "open to" -- growth orientation
  /\b(willingness|willing|open) to (take on|learn|grow|hear|share|change)\b/i,
  // "if you X" -- conditional growth nudge
  /\bif you (could|would|try|tried|focused|worked|put|spent)\b/i,
  // "instead of X, Y" -- redirection growth
  /\binstead of\b/i,
  // "even if" / "even though" -- hedged growth framing
  /\b(even if|even though|even when)\b/i,
  // Past-tense action you should do
  /\b(should have|could have|might have) (asked|tried|said|done|considered|focused|been|spoken|stepped)\b/i,
];

const EXTRA_NEGATIVE_PATTERNS = [
  // "wish you would have" -- counterfactual critique
  /\b(wish (you )?(would have|would of|had|could)|i wish)\b/i,
  /\b(barely|rarely|hardly) (contacted|reached out|attended|showed|contributed|participated|spoke|spoke up|engaged|responded)\b/i,
  /\b(didn't|did not|hasn't|has not|wasn't|was not) (really )?(contribute|participate|engage|show up|attend|respond|reach|put in|come prepared)\b/i,
  /\bat times (it felt|i felt|you|your|the team)\b/i,
  /\bcould have (put in|done|tried|engaged|been|focused|attended|shown)\b/i,
];

export function hasExtraGrowth(text) {
  if (!text) return false;
  return EXTRA_GROWTH_PATTERNS.some((p) => p.test(text));
}

export function hasExtraNegative(text) {
  if (!text) return false;
  return EXTRA_NEGATIVE_PATTERNS.some((p) => p.test(text));
}

/**
 * Adjust legacy tone score based on extra Spike G patterns.
 *
 * Rules (priority order):
 *   1. Extra negative + no clear positive/constructive => 0
 *   2. Extra growth + has_positive => 1.0
 *   3. Otherwise: legacy tone unchanged
 *
 * @param {number} legacyTone - the value from scoreTone() (0 | 0.5 | 1.0)
 * @param {object} toneEvidence - the second return value from scoreTone()
 * @param {string} text - the original (non-lowercased) comment
 * @returns {number} the adjusted tone score
 */
export function gradedTone(legacyTone, toneEvidence, text) {
  // The DO scoring.js toneEvidence uses bool fields: positive, growth,
  // constructive_tone, has_negative_tone, problematic_wrap_up. The
  // upstream Python prototype used array fields. Handle either shape.
  const ev = toneEvidence || {};
  const hasPositive = Boolean(ev.positive)
    || (Array.isArray(ev.positive_keywords) && ev.positive_keywords.length > 0);
  const hasGrowth = Boolean(ev.growth)
    || (Array.isArray(ev.growth_keywords) && ev.growth_keywords.length > 0);
  const hasConstr = Boolean(ev.constructive_tone)
    || (Array.isArray(ev.constructive_patterns) && ev.constructive_patterns.length > 0);

  const extraGrowth = hasExtraGrowth(text);
  const extraNeg = hasExtraNegative(text);

  if (extraNeg && !hasPositive && !hasConstr && legacyTone !== 0) {
    return 0.0;
  }
  if ((hasGrowth || extraGrowth) && hasPositive && legacyTone < 1.0) {
    return 1.0;
  }
  return legacyTone;
}
